// Prospectos (admin → Asesoras): todos los prospectos que entraron en un rango de
// fechas, su origen y si terminaron pagando. Complementa a "Pagos asesoras", que
// parte de los pagos. Los datos los arma GET /prospects/report en el servidor.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { DownloadIcon, SearchIcon } from '@chakra-ui/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import xhr from 'xhr';

import AiPagination from 'components/monitoring/ai/AiPagination';
import { StatusBadge } from 'pages/asesoras/components/ui';
import {
  PROSPECT_STATUS,
  channelLabel,
  deviceLabel,
  firstResponseInfo,
  formatDuration,
  howFoundLabel,
  programLabel,
  statusInfo,
} from 'pages/asesoras/prospectos/constants';
import {
  AdIdCell,
  AdviserSelect,
  BRAND,
  Dash,
  KpiTile,
  MATCH_LABEL,
  PAGE_SIZE,
  QuickRanges,
  RANGES,
  cop,
  downloadCsv,
  sourceDetail,
  sourceLabel,
  toInput,
  useActiveAdvisers,
  useUsdToCopRate,
} from './asesoras-shared';

const PAID_FILTERS = [
  { value: '', label: 'Todos' },
  { value: 'si', label: 'Pagaron' },
  { value: 'no', label: 'No han pagado' },
];

// Origen para agrupar la conversión: lo que respondió la persona y, si no hay,
// la fuente de la campaña (con los alias de Meta unificados).
const UTM_SOURCES = { fb: 'Facebook', facebook: 'Facebook', ig: 'Instagram', instagram: 'Instagram', google: 'Google', tiktok: 'TikTok' };
const originGroup = (row) => {
  if (row.how_found && row.how_found !== 'pendiente') return howFoundLabel(row.how_found);
  const source = String(row.utm_source || '').trim().toLowerCase();
  if (source) return `${UTM_SOURCES[source] || row.utm_source} (anuncio)`;
  return 'Sin dato';
};

// Fecha de ingreso: la de contacto que anota la asesora o, si no hay, la de registro.
const enteredAt = (row) => row.contact_date || row.created_at;

const median = (values) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
};
const percent = (part, total) => (total ? `${Math.round((part / total) * 100)} %` : '—');

const ProspectosAsesoras = () => {
  const advisers = useActiveAdvisers();
  const usdToCopRate = useUsdToCopRate();

  const [adviser, setAdviser] = useState('');
  const [from, setFrom] = useState(toInput(dayjs().startOf('month')));
  const [to, setTo] = useState(toInput(dayjs()));
  const [activeRange, setActiveRange] = useState('mes');
  const [query, setQuery] = useState('');
  const [paid, setPaid] = useState('');
  const [status, setStatus] = useState('');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async (params) => {
    setError('');
    if (!params.from || !params.to) {
      setError('Elige las fechas «Desde» y «Hasta».');
      return;
    }
    if (params.from > params.to) {
      setError('La fecha «Desde» no puede ser posterior a «Hasta».');
      return;
    }
    try {
      setLoading(true);
      const res = await xhr.get('/prospects/report', { params });
      setRows(res.data.rows || []);
      setPage(1);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error?.message || 'No se pudieron cargar los prospectos.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Al abrir: prospectos del mes actual de todas las asesoras.
  useEffect(() => {
    load({ from, to, adviser });
  }, []);

  const applyRange = (range) => {
    const [start, end] = range.range();
    setFrom(toInput(start));
    setTo(toInput(end));
    setActiveRange(range.key);
    load({ from: toInput(start), to: toInput(end), adviser });
  };

  const handleClear = () => {
    const [start, end] = RANGES[0].range();
    setAdviser('');
    setQuery('');
    setPaid('');
    setStatus('');
    setFrom(toInput(start));
    setTo(toInput(end));
    setActiveRange('mes');
    load({ from: toInput(start), to: toInput(end), adviser: '' });
  };

  // Valor de la venta en COP.
  const computed = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        saleCop: row.sale ? (row.sale.currency === 'USD' ? row.sale.amount * usdToCopRate : row.sale.amount) : 0,
      })),
    [rows, usdToCopRate]
  );

  // Búsqueda y estado: los KPI se calculan sobre esto (sin el filtro de pago,
  // para que la conversión siempre compare pagaron contra el total).
  const scoped = useMemo(() => {
    const q = query.trim().toLowerCase();
    return computed.filter((row) => {
      if (status && row.status !== status) return false;
      if (!q) return true;
      return [row.name, row.id, row.email, row.cellphone, row.adviser?.name, row.program, row.masivo, row.utm_campaign]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [computed, query, status]);

  const filtered = useMemo(() => {
    if (paid === 'si') return scoped.filter((row) => row.sale);
    if (paid === 'no') return scoped.filter((row) => !row.sale);
    return scoped;
  }, [scoped, paid]);

  const summary = useMemo(() => {
    const sales = scoped.filter((row) => row.sale);
    const days = sales.map((row) => row.sale.days_to_close).filter((d) => d != null);
    return {
      count: scoped.length,
      paid: sales.length,
      total: sales.reduce((acc, row) => acc + row.saleCop, 0),
      withoutAdviser: scoped.filter((row) => !row.adviser).length,
      untouched: scoped.filter((row) => row.status === 'nuevo').length,
      medianDays: median(days),
      avgDays: days.length ? Math.round(days.reduce((a, b) => a + b, 0) / days.length) : null,
      // Primera gestión: solo los que ya se midieron (desde que existe el dato).
      firstResponse: median(scoped.map((row) => row.first_response_minutes).filter((m) => m != null)),
      firstResponseCount: scoped.filter((row) => row.first_response_minutes != null).length,
      pendingFirst: scoped.filter((row) => firstResponseInfo(row).state === 'pending').length,
    };
  }, [scoped]);

  // Conversión por origen: dónde rinden más los prospectos.
  const bySource = useMemo(() => {
    const groups = new Map();
    scoped.forEach((row) => {
      const key = originGroup(row);
      const g = groups.get(key) || { key, count: 0, paid: 0 };
      g.count += 1;
      if (row.sale) g.paid += 1;
      groups.set(key, g);
    });
    return [...groups.values()].sort((a, b) => b.paid - a.paid || b.count - a.count).slice(0, 6);
  }, [scoped]);

  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const exportCsv = () => {
    const header = [
      'Fecha de ingreso', 'Registrado el', 'ID prospecto', 'Prospecto', 'Correo', 'Celular', 'Asesora', 'Estado', 'Programa', 'Canal',
      'Cómo nos conoció', 'Fuente', 'Medio', 'Campaña', 'Anuncio', 'Palabra clave', 'Dispositivo', 'Masivo', 'Pagó',
      'Fecha de pago', 'Código estudiante', 'Moneda', 'Valor', 'Valor (COP)', 'Días para cerrar', 'Estudiante encontrado',
      'Observación', 'Campaña registrada por asesora', 'Primera gestión', 'Primera gestión (minutos)', 'Primer estado',
    ];
    const lines = filtered.map((row) => [
      dayjs(enteredAt(row)).format('DD/MM/YYYY'),
      dayjs(row.created_at).format('DD/MM/YYYY h:mm A'),
      row.id,
      row.name,
      row.email,
      row.cellphone,
      row.adviser?.name || 'Sin asesora',
      statusInfo(row.status).label,
      row.program || '',
      channelLabel(row),
      howFoundLabel(row.how_found, row.how_found_detail),
      row.utm_source || '',
      row.utm_medium || '',
      row.utm_campaign || '',
      row.utm_content || '',
      row.utm_term || '',
      deviceLabel(row.utm_device),
      row.masivo || '',
      row.sale ? 'Sí' : 'No',
      row.sale ? dayjs(row.sale.payment_date).format('DD/MM/YYYY') : '',
      row.sale?.student_id || '',
      row.sale?.currency || '',
      row.sale?.amount ?? '',
      row.sale ? Math.round(row.saleCop) : '',
      row.sale?.days_to_close ?? '',
      row.sale ? { vinculo: 'Vinculado', correo: 'Por correo', celular: 'Por celular' }[row.sale.matched_by] : '',
      row.observation || '',
      row.utm_manual ? 'Sí' : '',
      row.first_status_change_at ? dayjs(row.first_status_change_at).format('DD/MM/YYYY h:mm A') : '',
      row.first_response_minutes ?? '',
      row.first_status ? statusInfo(row.first_status).label : '',
    ]);
    downloadCsv(header, lines, `prospectos-asesoras_${from}_${to}.csv`);
  };

  return (
    <Box p={{ base: 2, md: 4 }}>
      <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden" mb={5}>
        <Flex bg={BRAND} color="white" px={5} py={3} align="center" justify="space-between" gap={3} wrap="wrap">
          <Box>
            <Text fontSize="lg" fontWeight="700">
              Prospectos
            </Text>
            <Text fontSize="xs" opacity={0.85}>
              Todos los prospectos que entraron, su origen y si terminaron pagando.
            </Text>
          </Box>
          <Button
            size="sm"
            bg="white"
            color={BRAND}
            leftIcon={<DownloadIcon />}
            _hover={{ bg: 'whiteAlpha.900' }}
            onClick={exportCsv}
            isDisabled={loading || filtered.length === 0}
          >
            Exportar a Excel
          </Button>
        </Flex>

        <Box px={5} py={4}>
          <Grid templateColumns={{ base: '1fr', md: '2fr 1fr 1fr auto' }} gap={3} alignItems="end">
            <FormControl>
              <FormLabel fontSize="sm" color="gray.700">
                Asesora
              </FormLabel>
              <AdviserSelect advisers={advisers} value={adviser} onChange={setAdviser} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" color="gray.700">
                Ingresaron desde
              </FormLabel>
              <Input type="date" value={from} max={to} onChange={(e) => { setFrom(e.target.value); setActiveRange(''); }} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" color="gray.700">
                Hasta
              </FormLabel>
              <Input type="date" value={to} min={from} onChange={(e) => { setTo(e.target.value); setActiveRange(''); }} />
            </FormControl>
            <Flex gap={2}>
              <Button bg={BRAND} color="white" _hover={{ bg: '#463fd1' }} onClick={() => load({ from, to, adviser })} isLoading={loading}>
                Buscar
              </Button>
              <Button variant="outline" onClick={handleClear}>
                Limpiar
              </Button>
            </Flex>
          </Grid>

          <QuickRanges active={activeRange} onSelect={applyRange} />

          {error && (
            <Text mt={3} fontSize="sm" color="red.600">
              {error}
            </Text>
          )}
        </Box>
      </Box>

      <Grid templateColumns={{ base: '1fr 1fr', lg: 'repeat(5, 1fr)' }} gap={3} mb={3}>
        <KpiTile
          label="Prospectos"
          value={summary.count.toLocaleString('es-CO')}
          hint={`${summary.untouched} sin gestionar · ${summary.withoutAdviser} sin asesora`}
        />
        <KpiTile label="Pagaron" value={summary.paid.toLocaleString('es-CO')} hint={`Conversión ${percent(summary.paid, summary.count)}`} />
        <KpiTile label="Ventas" value={cop(summary.total)} hint="Primer pago de cada venta" />
        <KpiTile
          label="Días para cerrar"
          value={summary.medianDays != null ? `${summary.medianDays} d` : '—'}
          hint={summary.avgDays != null ? `Mediana · promedio ${summary.avgDays} d` : 'Sin ventas en el rango'}
        />
        <KpiTile
          label="Primera gestión"
          value={summary.firstResponse != null ? formatDuration(summary.firstResponse) : '—'}
          hint={`Mediana de ${summary.firstResponseCount} · ${summary.pendingFirst} sin gestionar`}
        />
      </Grid>

      {bySource.length > 0 && (
        <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" px={5} py={4} mb={5}>
          <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.06em" mb={3}>
            Conversión por origen
          </Text>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }} columnGap={8} rowGap={3}>
            {bySource.map((g) => (
              <Box key={g.key}>
                <Flex justify="space-between" fontSize="sm" mb={1}>
                  <Text color="gray.700" noOfLines={1}>
                    {g.key}
                  </Text>
                  <Text color="gray.600" sx={{ fontVariantNumeric: 'tabular-nums' }} whiteSpace="nowrap">
                    {g.paid} de {g.count} · <b>{percent(g.paid, g.count)}</b>
                  </Text>
                </Flex>
                <Box h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                  <Box h="100%" bg={BRAND} borderRadius="full" w={`${g.count ? (g.paid / g.count) * 100 : 0}%`} minW={g.paid ? '4px' : 0} />
                </Box>
              </Box>
            ))}
          </Grid>
        </Box>
      )}

      <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
        <Flex px={5} py={3} align="center" justify="space-between" gap={3} wrap="wrap" borderBottom="1px solid" borderColor="gray.100">
          <Flex gap={3} wrap="wrap" align="center" flex="1">
            <InputGroup maxW="320px" size="sm">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Buscar nombre, ID, correo, celular, asesora…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(1);
                }}
              />
            </InputGroup>
            <Select
              size="sm"
              maxW="200px"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todos los estados</option>
              {PROSPECT_STATUS.map((el) => (
                <option key={el.value} value={el.value}>
                  {el.label}
                </option>
              ))}
            </Select>
            <ButtonGroup size="sm" isAttached variant="outline">
              {PAID_FILTERS.map((el) => (
                <Button
                  key={el.value}
                  colorScheme={paid === el.value ? 'purple' : 'gray'}
                  variant={paid === el.value ? 'solid' : 'outline'}
                  onClick={() => {
                    setPaid(el.value);
                    setPage(1);
                  }}
                >
                  {el.label}
                </Button>
              ))}
            </ButtonGroup>
          </Flex>
          <Text fontSize="xs" color="gray.500">
            Valores en USD convertidos a COP con tasa {Math.round(usdToCopRate).toLocaleString('es-CO')}
          </Text>
        </Flex>

        {loading ? (
          <Flex justify="center" py={16}>
            <Spinner color={BRAND} />
          </Flex>
        ) : filtered.length === 0 ? (
          <Box textAlign="center" py={14} px={4}>
            <Text fontWeight="600" color="gray.700">
              No hay prospectos para estos filtros
            </Text>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Prueba con otro rango de fechas, otro estado o «Todos».
            </Text>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th colSpan={6} bg="gray.50" color="gray.600" borderColor="gray.200">
                      Prospecto
                    </Th>
                    <Th colSpan={4} bg="purple.50" color="purple.700" borderColor="purple.100">
                      Origen
                    </Th>
                    <Th colSpan={3} bg="green.50" color="green.700" borderColor="green.100">
                      Venta
                    </Th>
                  </Tr>
                  <Tr>
                    <Th bg="gray.50">Ingreso</Th>
                    <Th bg="gray.50">Prospecto</Th>
                    <Th bg="gray.50">Asesora</Th>
                    <Th bg="gray.50">Estado</Th>
                    <Th bg="gray.50">Primera gestión</Th>
                    <Th bg="gray.50">Programa</Th>
                    <Th bg="purple.50">Cómo nos conoció</Th>
                    <Th bg="purple.50">Fuente (UTM)</Th>
                    <Th bg="purple.50">ID anuncio</Th>
                    <Th bg="purple.50">Masivo</Th>
                    <Th bg="green.50">Pagó</Th>
                    <Th bg="green.50" isNumeric>
                      Valor
                    </Th>
                    <Th bg="green.50" isNumeric>
                      Días para cerrar
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {pageRows.map((row) => {
                    const info = statusInfo(row.status);
                    return (
                      <Tr key={row.id} _hover={{ bg: 'gray.50' }}>
                        <Td whiteSpace="nowrap">
                          {dayjs(enteredAt(row)).format('DD/MM/YYYY')}
                          <Text fontSize="xs" color="gray.500">
                            {/* La fecha de contacto no tiene hora: se muestra la del registro. */}
                            {row.contact_date
                              ? `Registro ${dayjs(row.created_at).format('DD/MM h:mm A')}`
                              : dayjs(row.created_at).format('h:mm A')}
                          </Text>
                        </Td>
                        <Td minW="210px" whiteSpace="normal">
                          <Text fontWeight="600" color="gray.800">
                            {row.name || 'Sin nombre'}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            #{row.id} · {row.cellphone || row.email || 'Sin contacto'}
                          </Text>
                        </Td>
                        <Td minW="150px" whiteSpace="normal">
                          {row.adviser ? (
                            row.adviser.name
                          ) : (
                            <Badge colorScheme="orange" variant="subtle" textTransform="none">
                              Sin asesora
                            </Badge>
                          )}
                        </Td>
                        <Td>
                          <StatusBadge label={info.label} color={info.color} />
                        </Td>
                        <Td whiteSpace="nowrap" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {(() => {
                            const first = firstResponseInfo(row);
                            if (first.state === 'done') return formatDuration(first.minutes);
                            if (first.state === 'pending')
                              return (
                                <Text fontSize="sm" color="orange.500">
                                  Sin gestionar
                                </Text>
                              );
                            return <Dash />;
                          })()}
                        </Td>
                        <Td minW="160px" whiteSpace="normal">
                          {row.program ? (
                            <Tooltip hasArrow label={row.program}>
                              <Text>{programLabel(row.program)}</Text>
                            </Tooltip>
                          ) : (
                            <Dash />
                          )}
                          <Text fontSize="xs" color="gray.500">
                            {channelLabel(row)}
                          </Text>
                        </Td>
                        <Td minW="150px" whiteSpace="normal">
                          {row.how_found ? howFoundLabel(row.how_found, row.how_found_detail) : <Dash />}
                        </Td>
                        <Td minW="140px" whiteSpace="normal">
                          {sourceLabel(row) ? (
                            <>
                              <Text>{sourceLabel(row)}</Text>
                              {sourceDetail(row) && (
                                <Text fontSize="xs" color="gray.500">
                                  {sourceDetail(row)}
                                </Text>
                              )}
                            </>
                          ) : (
                            <Dash />
                          )}
                        </Td>
                        <Td minW="150px" whiteSpace="normal">
                          <AdIdCell origin={row} />
                        </Td>
                        <Td minW="130px" whiteSpace="normal">
                          {row.masivo || <Dash />}
                        </Td>
                        <Td minW="150px" whiteSpace="normal">
                          {row.sale ? (
                            <>
                              <Badge colorScheme="green" variant="subtle" textTransform="none" fontWeight="600">
                                Sí · {dayjs(row.sale.payment_date).format('DD/MM/YYYY')}
                              </Badge>
                              <Flex gap={1} mt={1} align="center" wrap="wrap">
                                <Link to={`/plataforma/administrador/ver-estudiante/${row.sale.student_id}`}>
                                  <Text fontSize="xs" color={BRAND} _hover={{ textDecoration: 'underline' }}>
                                    Código {row.sale.student_id}
                                  </Text>
                                </Link>
                                {MATCH_LABEL[row.sale.matched_by] && (
                                  <Tooltip
                                    hasArrow
                                    label="El prospecto no está vinculado al estudiante; se encontró por su mismo correo o celular."
                                  >
                                    <Text fontSize="xs" color="gray.500">
                                      ({MATCH_LABEL[row.sale.matched_by]})
                                    </Text>
                                  </Tooltip>
                                )}
                              </Flex>
                            </>
                          ) : (
                            <Badge colorScheme="gray" variant="subtle" textTransform="none" fontWeight="500">
                              No
                            </Badge>
                          )}
                        </Td>
                        <Td isNumeric sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {row.sale ? (
                            <>
                              {cop(row.saleCop)}
                              {row.sale.currency === 'USD' && (
                                <Text fontSize="xs" color="gray.500">
                                  USD {row.sale.amount.toLocaleString('es-CO')}
                                </Text>
                              )}
                            </>
                          ) : (
                            <Dash />
                          )}
                        </Td>
                        <Td isNumeric sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {row.sale?.days_to_close != null ? `${row.sale.days_to_close} d` : <Dash />}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
            <AiPagination
              pagination={{ page, pageSize: PAGE_SIZE, total: filtered.length, pageCount }}
              noun="prospectos"
              onPageChange={setPage}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default ProspectosAsesoras;
